/**
 * AttendanceTypeSelector.tsx
 *
 * Primeira tela do fluxo de criação de agenda.
 * O usuário escolhe o tipo de atendimento entre:
 *  - Presencial: atendimento físico com endereço
 *  - Teleatendimento: consulta por vídeo (apenas 1 por organização)
 *  - Chat: atendimento por mensagens (apenas 1 por organização)
 *  - Domicílio: atendimento na casa do paciente (endereço preenchido depois)
 *  - Teleconsultoria: horários de teleconsultor (só com a extensão ativa)
 *
 * Regras de negócio:
 *  - Teleatendimento e Chat permitem somente 1 localização por organização.
 *    Se já existe uma ativa, informa o usuário mas permite continuar usando-a.
 *  - Domicílio e Presencial permitem múltiplas localizações.
 */

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import {
  Videocam,
  Chat,
  LocationOn,
  Home,
  VideoCameraFront,
} from "@mui/icons-material";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import { useTeleconsultoriaProfile } from "src-next/modules/teleconsultoria/profile/TeleconsultoriaProfileContext";
import { AttendanceType } from "../types";

interface AttendanceTypeSelectorProps {
  /** Tipo atualmente selecionado */
  value: AttendanceType | null;
  /** Callback ao mudar o tipo */
  onChange: (type: AttendanceType) => void;
  /**
   * Avança para o próximo step.
   * Recebe o tipo recém-selecionado para que o pai possa calcular o stepMap
   * correto sem depender do estado assíncrono do React.
   */
  onNext: (type: AttendanceType) => void;
}

const ATTENDANCE_OPTIONS: {
  type: AttendanceType;
  label: string;
  description: string;
  icon: React.ReactNode;
  colorKey: string;
  bgKey: string;
}[] = [
  {
    type: "presencial",
    label: "Presencial",
    description: "Atendimento no consultório ou clínica com endereço físico.",
    icon: <LocationOn sx={{ fontSize: 40 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
  },
  {
    type: "teleatendimento",
    label: "Teleatendimento",
    description: "Consulta por vídeo online.",
    icon: <Videocam sx={{ fontSize: 40 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
  },
  {
    type: "chat",
    label: "Chat",
    description: "Atendimento por mensagens online.",
    icon: <Chat sx={{ fontSize: 40 }} />,
    // Não há roxo no tema; error (vinho/vermelho-arroxeado) é o mais próximo
    colorKey: "error700.main",
    bgKey: "error100.main",
  },
  {
    type: "domicilio",
    label: "Atendimento a Domicílio",
    description: "Atendimento na casa do paciente. Endereço será configurado posteriormente.",
    icon: <Home sx={{ fontSize: 40 }} />,
    colorKey: "warning700.main",
    bgKey: "warning100.main",
  },
];

/**
 * Card extra, separado da lista fixa: só entra quando a organização tem a
 * extensão Teleconsultoria ativa. Assim os 4 cards originais não mudam.
 */
const TELECONSULTORIA_OPTION: (typeof ATTENDANCE_OPTIONS)[number] = {
  type: "teleconsultoria",
  label: "Teleconsultoria",
  description: "Horários para teleconsultoria por vídeo com outras clínicas.",
  icon: <VideoCameraFront sx={{ fontSize: 40 }} />,
  colorKey: "primary700.main",
  bgKey: "primary100.main",
};

export function AttendanceTypeSelector({
  onChange,
  onNext,
}: AttendanceTypeSelectorProps) {
  const { contractTeleconsultoria } = useTeleconsultoriaContractContext();
  // Agenda de teleconsultoria é da prestadora: afiliada não cria, mesmo com contrato.
  const { canManageAvailability } = useTeleconsultoriaProfile();
  const visibleOptions = contractTeleconsultoria && canManageAvailability
    ? [...ATTENDANCE_OPTIONS, TELECONSULTORIA_OPTION]
    : ATTENDANCE_OPTIONS;

  return (
    <Stack spacing={3} width="100%">
      <Typography
        variant="h6"
        fontWeight="600"
        textAlign="center"
        color="neutral700.main"
        py={2}
      >
        Escolha o tipo de atendimento para esta agenda
      </Typography>

      {/* Grid de cards de tipo */}
      <Box
        display="grid"
        // Uma coluna por card no desktop: com 4 cards fica igual a antes.
        gridTemplateColumns={{
          xs: "1fr 1fr",
          md: `repeat(${visibleOptions.length}, 1fr)`,
        }}
        gap={2}
      >
        {visibleOptions.map((option) => (
            <Card
              key={option.type}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1.5px solid",
                borderColor: "grey.200",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
                "&:hover": {
                  boxShadow: `0 4px 16px rgba(0,0,0,0.13)`,
                  borderColor: option.colorKey,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  // Atualiza o estado e avança passando o tipo diretamente —
                  // evita depender do setState assíncrono para calcular o próximo step
                  onChange(option.type);
                  onNext(option.type);
                }}
                sx={{ height: "100%", borderRadius: 3 }}
              >
                <CardContent sx={{ py: 3 }}>
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    {/* Ícone colorido */}
                    <Box
                      sx={{
                        backgroundColor: option.bgKey,
                        borderRadius: "50%",
                        p: 1.5,
                        color: option.colorKey,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {option.icon}
                    </Box>

                    {/* Nome do tipo */}
                    <Typography variant="subtitle1" fontWeight={700}>
                      {option.label}
                    </Typography>

                    {/* Descrição */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={12}
                      lineHeight={1.5}
                    >
                      {option.description}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
      </Box>

    </Stack>
  );
}
